import { FC, FocusEventHandler, MouseEvent, MouseEventHandler, PropsWithChildren, RefCallback, TouchEventHandler, useCallback, useEffect, useRef, useState } from "react";
import { BounceLoader } from "react-spinners";
import { Body } from "capitalrx-components-2.0/lib/typography";
import classNames from "classnames";
import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Tooltip, TooltipProps } from "../Tooltip";
import styles from "./styles.module.scss";

/**
 * The internal tab component properties.
 */
export interface InternalTabProps extends BaseProps {
  /**
   * The tab ID.
   */
  readonly id: string;

  /**
   * Indicates whether the tab is disabled.
   */
  readonly disabled?: boolean;

  /**
   * The tab tooltip.
   */
  readonly tooltip?: TooltipProps["content"];

  /**
   * The tooltip appearance.
   */
  readonly tooltipAppearance?: TooltipProps["appearance"];

  /**
   * The tooltip alignment.
   */
  readonly tooltipAlignment?: TooltipProps["alignment"];

  /**
   * Indicates whether the current tab is in an error state.
   */
  readonly hasError?: boolean;

  /**
   * Indicates whether the current tab is busy.
   */
  readonly isBusy?: boolean;

  /**
   * Indicates whether the current tab is selected.
   */
  readonly isSelected?: boolean;

  /**
   * The parent tabs test ID.
   */
  readonly parentTestId?: string;

  /**
   * Called when the tab is focused.
   */
  readonly onFocus?: FocusEventHandler;

  /**
   * Called when the mouse is clicked.
   */
  readonly onClick?: MouseEventHandler;

  /**
   * Called when the mouse button is pressed.
   */
  readonly onMouseDown?: MouseEventHandler;

  /**
   * Called when a touch event starts.
   */
  readonly onTouchStart?: TouchEventHandler;
}

/**
 * The internal tab component to render.
 */
export const InternalTab: FC<PropsWithChildren<InternalTabProps>> = (props) => {
  const {
    id,
    hasError = false,
    isBusy = false,
    children,
    parentTestId,
    isSelected = false,
    disabled = false,
    tooltip,
    tooltipAlignment,
    tooltipAppearance,
    onClick,
    onMouseDown,
    onTouchStart,
    onFocus,
  } = props;

  const [showError, setShowError] = useState(false);
  const activeErrorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disabledRef = useRef<boolean>(disabled);

  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  const tabBaseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      styles.Tab,
      "tab",
      {
        selected: isSelected,
        error: hasError,
        disabled: disabled,
        "show-error": showError,
        busy: isBusy,
      },
    ],
    testIdPrefix: parentTestId ? `${parentTestId}-tab` : "tab-set-tab",
  });

  useEffect(() => {
    if (!hasError) return;

    if (activeErrorTimeoutRef.current) {
      clearTimeout(activeErrorTimeoutRef.current);
    }

    setShowError(true);
    activeErrorTimeoutRef.current = setTimeout(() => {
      setShowError(false);
      activeErrorTimeoutRef.current = null;
    }, 1000);
  }, [hasError]);

  const mouseUpHandler = useCallback((ev: MouseEvent<HTMLElement>) => {
    if (disabledRef.current) return;
    if (ev.button !== 0) return;
    ev.preventDefault();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const tabs = Array.from(document.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([aria-disabled="true"])'));
    const currentIndex = tabs.findIndex((tab) => tab === e.currentTarget);
    if (currentIndex === -1) return;
    let nextIndex = currentIndex;

    switch (e.key) {
      case "ArrowRight":
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case "ArrowLeft":
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    e.preventDefault();
    tabs[nextIndex]?.focus();
  };

  const buttonContent = (ref?: RefCallback<HTMLElement>) => (
    <button
      ref={ref}
      role="tab"
      id={`tab-${id}`}
      aria-selected={isSelected}
      aria-controls={`tabpanel-${id}`}
      aria-disabled={disabled}
      aria-busy={isBusy}
      disabled={disabled}
      className={classNames(styles.HotSpot, "hotspot")}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseUp={mouseUpHandler}
      onTouchStart={onTouchStart}
      onFocus={onFocus}
      data-tabid={id}
      tabIndex={isSelected ? 0 : -1}
      onKeyDown={(e) => handleKeyDown(e)}
    >
      <Body inline size="inherit" className={classNames(styles.Content, "content")}>
        {isBusy && (
          <span className={classNames(styles.Loader, "loader")}>
            <BounceLoader loading size="1em" />
          </span>
        )}
        <span className={classNames(styles.ClientArea, "client-area")}>{children}</span>
      </Body>
    </button>
  );

  return (
    <li {...tabBaseHtmlProps} role="presentation">
      {tooltip && (
        <Tooltip content={tooltip} alignment={tooltipAlignment} appearance={tooltipAppearance}>
          {(ref) => buttonContent(ref)}
        </Tooltip>
      )}
      {!tooltip && buttonContent()}
    </li>
  );
};
